// collector/previewReplay.js
//
// Record and replay of a shop's supplier preview (supplier-preview-offline
// P2-A, locked answer L-E; design Section 2.B). ONE transport (`adapter`) on
// the one axios instance (httpConfig/config/axios.js) — every read of the app
// passes here, and the JSON:API decoding runs AFTER it, so answering from a
// saved copy feeds every screen unchanged.
//
//   record  the morning download reads a shop's first pages with the header
//           `X-Poolit-Preview-Shop: <shopId>` (the server answers them as
//           under a live preview of that shop); each answer carrying the
//           header of the shop being recorded is kept, raw, under its
//           `requestKey`. Nothing else the app reads meanwhile is kept.
//   replay  on while the marker `poolit:preview:replay` = {shopId, recordedAt}
//           is set (an offline preview, L-A). A GET is answered from that
//           shop's copy (status 200) or fails like a network error — no
//           `response` — and is logged `[preview] miss <key>`, so each screen
//           shows its own no-signal state. A GET NEVER reaches the network in
//           replay. Any other method goes to the network as usual (the
//           collector's own queues; writes in the shop's name are already
//           hidden on the supplier screens and refused by the server).
//
// Kept small and free of static imports beyond axios: it sits in the boot
// bundle (axios.js installs it). The copies' store is loaded on first replay.
import axios, { AxiosError } from 'axios';

/** The read-only header the server honours on a collector's GET (backend L-B). */
export const PREVIEW_HEADER = 'X-Poolit-Preview-Shop';
/** The offline preview's marker (L-A): `{shopId, recordedAt}`. */
export const REPLAY_MARKER_KEY = 'poolit:preview:replay';
/** The collector's own `poolit:auth`, saved at enter and put back at exit (L-A, L-K). */
export const OWN_AUTH_KEY = 'poolit:preview:own-auth';
/** A live preview closed with no signal: its DELETE is still owed to the server (L-K). */
export const EXIT_PENDING_KEY = 'poolit:preview:exit-pending';

/** The live preview's DELETE is still owed (collector/previewLeads.js sends it). */
export function previewExitPending() {
  try {
    return localStorage.getItem(EXIT_PENDING_KEY) != null;
  } catch {
    return false;
  }
}

/** A copy older than this is never opened (L-H) — and a marker that old is ignored. */
export const COPY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The marker, or null (none, unreadable, or naming a copy over 24 hours old —
 * a marker left by a tab that died mid-preview must not starve every read).
 */
export function readReplayMarker() {
  try {
    const raw = localStorage.getItem(REPLAY_MARKER_KEY);
    if (!raw) return null;
    const marker = JSON.parse(raw);
    const recordedAt = Date.parse(marker?.recordedAt ?? '');
    if (marker?.shopId == null || !(Date.now() - recordedAt < COPY_MAX_AGE_MS)) return null;
    return { shopId: String(marker.shopId), recordedAt: marker.recordedAt };
  } catch {
    return null;
  }
}

/** An offline preview is on screen: reads come from the phone. */
export function isReplayOn() {
  return readReplayMarker() != null;
}

function stripBase(url, baseURL) {
  if (baseURL && url.startsWith(baseURL)) return url.slice(baseURL.length) || '/';
  return url;
}

/**
 * The name a read is saved under: method + path (relative to the API base) +
 * the query sorted by key, without the `_t` cache-buster every request carries
 * (api-interceptors.js) — e.g. `GET /supplier-boxes?fields[supplier-boxes]=…&page[limit]=20`.
 */
export function requestKey(config) {
  const method = String(config?.method ?? 'get').toUpperCase();
  const raw = stripBase(String(config?.url ?? ''), config?.baseURL);
  const [path, inline = ''] = raw.split('?');
  const pairs = [...new URLSearchParams(inline)];
  for (const [name, value] of Object.entries(config?.params ?? {})) {
    if (value === undefined || value === null) continue; // axios leaves these out too
    pairs.push([name, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
  }
  const sorted = pairs
    .filter(([name]) => name !== '_t')
    .map(([name, value]) => `${name}=${value}`)
    .sort();
  return sorted.length ? `${method} ${path}?${sorted.join('&')}` : `${method} ${path}`;
}

function headerOf(config, name) {
  const headers = config?.headers;
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name) ?? undefined;
  return headers[name];
}

// ── Record ───────────────────────────────────────────────────────────────────

/** The shop being recorded and what it answered so far. */
let recording = null;

/** Keep, from now on, the answers to reads carrying this shop's header. */
export function startRecording(shopId) {
  recording = { shopId: String(shopId), responses: {} };
}

/** Stop keeping; returns `{requestKey: rawBody}` of the reads that answered. */
export function stopRecording() {
  const responses = recording?.responses ?? {};
  recording = null;
  return responses;
}

// ── The recorder (L-F) ───────────────────────────────────────────────────────

/** «أيامك»: at most this many pages while the showroom filter hides every day so far. */
export const RECORD_MAX_BOX_PAGES = 3;
/** The detail of this many of the most recent days shown is saved. */
export const RECORD_BOX_DETAILS = 10;

let recordChain = Promise.resolve();

/**
 * Save a copy of one shop's supplier preview: the first page of every screen,
 * read as the server answers that shop's live preview (the header), one read
 * at a time, with the endpoints and first-page arguments the screens use
 * (shared constants — the parity test mounts each page over the copy). Writes
 * the row only when EVERY read answered; any failure keeps the previous copy
 * and rejects. Calls queue: one shop records at a time.
 *
 * Saved: profile; wall page 1; the headline; «ناقص» page 1; «أيامك» page 1
 * (+ up to two more pages while the showroom filter hides every day so far);
 * the detail of the 10 most recent days shown; «السوق» page 1; «اهتمام
 * السيلرز» page 1; Poola's current deal; its offers page 1 (if a deal); the
 * club PoolShare total. Not saved: next pages, a wall post's detail, the
 * invoice sheet, older days.
 *
 * @param {string|number} shopId - the shop's org id
 * @param {{ source?: 'day'|'picked', name?: string|null, pickedAt?: string|null }} [options] -
 *   `pickedAt`: a shop picked now («عروض المحلات»); a refresh keeps the row's own
 * @returns {Promise<object>} the row as stored
 */
export function recordShop(shopId, options = {}) {
  const run = () => recordOne(String(shopId), options);
  const next = recordChain.then(run, run);
  recordChain = next.catch(() => {});
  return next;
}

async function recordOne(shopId, { source = 'day', name = null, pickedAt = null } = {}) {
  const [
    { jsonApiClient },
    { PROFILE_INCLUDE },
    wall,
    missing,
    boxes,
    feed,
    { CURRENT_DEAL_ENDPOINT },
    { OFFERS_ENDPOINT },
    { POOLA_OFFERS_PAGE_LIMIT },
    { clubPoolShareTotalService },
    { previewStore },
  ] = await Promise.all([
    import('@/httpConfig/config/axios.js'),
    import('@/composables/useAuth.js'),
    import('@/composables/useSupplierWallPosts.js'),
    import('@/composables/useSupplierMissingProducts.js'),
    import('@/composables/useSupplierDailyOrders.js'),
    import('@/services/supplierFeed.service.js'),
    import('@/composables/usePoolaDeal.js'),
    import('@/composables/usePoolaOffers.js'),
    import('@/constants/poola.js'),
    import('@/services/clubPoolShareTotal.service.js'),
    import('@/collector/previewStore.js'),
  ]);
  const headers = { [PREVIEW_HEADER]: shopId };
  const get = (url, params = {}) => jsonApiClient.get(url, { params, headers });

  startRecording(shopId);
  let profileUser;
  let responses = {};
  try {
    profileUser = (await get('/users/-actions/profile', { include: PROFILE_INCLUDE }))?.data?.data;
    // The server ignored the header (not a collector any more, not a shop):
    // that answer is his own profile, never a shop's copy.
    const shop = profileUser?.meta?.supplierPreview?.shop;
    if (profileUser?.meta?.supplierPreview?.active !== true || String(shop?.id) !== shopId) {
      throw new Error(`[preview] the server did not answer as shop ${shopId}`);
    }

    await get(wall.POSTS_ENDPOINT, { 'page[limit]': wall.PAGE_LIMIT });
    // SupplierWallPostsPage `loadSegmentCounts`.
    await get('/suppliers-club/headline');
    await get(missing.ENDPOINT, { 'page[number]': 1, 'page[size]': missing.PAGE_SIZE });

    // «أيامك»: the page walks on by itself while every day so far is hidden.
    const shown = [];
    let cursor = null;
    for (let page = 0; page < RECORD_MAX_BOX_PAGES; page += 1) {
      const params = { 'page[limit]': boxes.BOX_PAGE_LIMIT, 'fields[supplier-boxes]': boxes.BOX_FIELDS };
      if (cursor) params['page[after]'] = cursor;
      const body = (await get(boxes.BOXES_ENDPOINT, params))?.data;
      shown.push(...(body?.data ?? []).filter(boxes.isShownToShowroom));
      cursor = body?.meta?.page?.to ?? null;
      if (shown.length > 0 || !body?.meta?.page?.hasMore || !cursor) break;
    }
    for (const box of shown.slice(0, RECORD_BOX_DETAILS)) {
      await get(`${boxes.BOXES_ENDPOINT}/${box.id}`, { include: boxes.BOX_DETAIL_INCLUDE });
    }

    const feedParams = {
      include: feed.SUPPLIER_FEED_INCLUDE,
      withCount: feed.SUPPLIER_FEED_WITH_COUNT,
      'page[limit]': feed.SUPPLIER_FEED_PAGE_SIZE,
    };
    await get(feed.SUPPLIER_FEED_ENDPOINTS.posts(), feedParams);
    await get(feed.SUPPLIER_FEED_ENDPOINTS.interest(), feedParams);

    const deal = (await get(CURRENT_DEAL_ENDPOINT))?.data?.data;
    if (deal?.id != null) {
      await get(OFFERS_ENDPOINT, { 'filter[deal]': deal.id, 'page[limit]': POOLA_OFFERS_PAGE_LIMIT });
    }
    await clubPoolShareTotalService.get({ params: {}, headers });
  } finally {
    responses = stopRecording();
  }

  const store = previewStore();
  const existing = await store.get(shopId).catch(() => null);
  const picked = existing?.source === 'picked' || source === 'picked';
  const row = {
    shopId,
    name: profileUser?.meta?.supplierPreview?.shop?.name || name || existing?.name || '',
    source: picked ? 'picked' : 'day',
    ...(picked ? { pickedAt: pickedAt ?? existing?.pickedAt ?? new Date().toISOString() } : {}),
    recordedAt: new Date().toISOString(),
    responses,
    photoUrls: existing?.photoUrls ?? [],
    lead: existing?.lead ?? null,
  };
  await store.put(row);
  return row;
}

// ── Replay ───────────────────────────────────────────────────────────────────

/** The copy being replayed, read once per shop (a row is 150–300 KB). */
let replayRow = null;

async function replayResponses(shopId) {
  if (replayRow?.shopId !== shopId) {
    // One retry: a slow first open (its 3 s ceiling) must not turn a whole screen into misses.
    const promise = import('@/collector/previewStore.js')
      .then(({ previewStore }) => previewStore().get(shopId).catch(() => previewStore().get(shopId)))
      .then((row) => row?.responses ?? {});
    replayRow = { shopId, promise };
    // A failed read is not kept: the next request asks the phone again.
    promise.catch(() => {
      if (replayRow?.promise === promise) replayRow = null;
    });
  }
  return replayRow.promise;
}

async function replay(config, shopId) {
  const key = requestKey(config);
  let responses = {};
  try {
    responses = await replayResponses(shopId);
  } catch (error) {
    console.warn('[preview] the copy could not be read', error);
  }
  if (Object.prototype.hasOwnProperty.call(responses, key)) {
    return { data: responses[key], status: 200, statusText: 'OK', headers: {}, config, request: {} };
  }
  console.warn(`[preview] miss ${key}`);
  // A network error's shape: no `response`, so every caller takes its no-signal path.
  // No `request` either: the response interceptor then only logs (no network-error event).
  throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, config);
}

// ── Photos the phone does not hold (L-J) ─────────────────────────────────────

const MISSING_IMAGE_CLASS = 'preview-img-missing';
let removeImageListener = null;

/**
 * While an offline preview is on screen, a picture that is not on the phone is
 * hidden (the card keeps its text) instead of showing a broken-image icon to
 * the shop owner: ONE capture-phase `error` listener on the document (an
 * <img>'s error does not bubble). Idempotent; `stopHidingMissingImages` removes it.
 */
export function hideMissingImages() {
  if (removeImageListener || typeof document === 'undefined') return;
  const onError = (event) => {
    if (event.target?.tagName === 'IMG') event.target.classList.add(MISSING_IMAGE_CLASS);
  };
  document.addEventListener('error', onError, true);
  removeImageListener = () => document.removeEventListener('error', onError, true);
}

export function stopHidingMissingImages() {
  removeImageListener?.();
  removeImageListener = null;
}

// ── The transport ────────────────────────────────────────────────────────────

let networkAdapter = null;

/** The one adapter: replay a GET while the marker is set, else the network (recording what is asked). */
export async function previewAdapter(config) {
  const isGet = String(config?.method ?? 'get').toLowerCase() === 'get';
  const marker = isGet ? readReplayMarker() : null;
  if (marker) return replay(config, marker.shopId);

  const response = await networkAdapter(config);
  if (isGet && recording && String(headerOf(config, PREVIEW_HEADER) ?? '') === recording.shopId) {
    recording.responses[requestKey(config)] = response.data;
  }
  return response;
}

/** Install the transport on the base axios instance (once, axios.js). */
export function installPreviewTransport(instance) {
  networkAdapter = axios.getAdapter(instance.defaults.adapter);
  instance.defaults.adapter = previewAdapter;
}

/** Tests only: the network behind the transport, and a clean slate. */
export function setNetworkAdapterForTests(adapter) {
  networkAdapter = adapter;
}

export function resetPreviewReplayForTests() {
  recording = null;
  replayRow = null;
  stopHidingMissingImages();
  try {
    localStorage.removeItem(REPLAY_MARKER_KEY);
    localStorage.removeItem(OWN_AUTH_KEY);
    localStorage.removeItem(EXIT_PENDING_KEY);
  } catch {
    /* nothing to clear */
  }
}
